<template>
  <div :class="['owc-lightbox', { 'owc-lightbox--closed': !isOpen }]">
    <ValidationBoxWrapper v-if="isEditMode">
      <slot slot="validation-box" name="validation-box" />
    </ValidationBoxWrapper>
    <div class="wb-grid-container">
      <slot></slot>
    </div>
    <button
      @click="closeLightbox"
      type="button"
      class="owc-lightbox__close-button wb-round-button wb-round-button--solid-level-0 wb-round-button--medium"
    >
      <wb-icon class="wb-round-button__icon" aria-hidden="true" name="close"></wb-icon>
      Close
    </button>
  </div>
</template>

<script lang="ts">
/**
 * `Lightbox` - A simple lightbox that contains a close button and the content provided via slot.
 */
import ComponentInitializeDispatcher from '@owc-utils/Mixins/ComponentInitializeDispatcher';
import { LIGHTBOX_EVENTS, LIGHTBOX_CLASSES, FOCUS_STACK_LIGHTBOX_CONTEXT } from './Lightbox.config';
import getAccessibilityStackInstance from '@owc-utils/common/AccessibilityFocusStackHelper';
import ValidationBoxWrapper from '@owc-packages/ValidationBox/src';

const accessibilityHelper = getAccessibilityStackInstance();

export default ComponentInitializeDispatcher.extend({
  name: 'owc-lightbox',
  components: {
    ValidationBoxWrapper,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scrollLockElements: [document.body, document.documentElement] as HTMLElement[],
    };
  },
  methods: {
    closeLightbox(): void {
      this.$emit(LIGHTBOX_EVENTS.close);
      this.unlockWindow();
    },
    lockWindow(): void {
      this.scrollLockElements.forEach(element => element.classList.add(LIGHTBOX_CLASSES.lockClass));
    },
    unlockWindow(): void {
      this.scrollLockElements.forEach(element => {
        element.classList.remove(LIGHTBOX_CLASSES.lockClass);
      });
    },
    bindKeyboardKeys(): void {
      document.addEventListener('keydown', this.handleKeyboardKeys);

      this.$nextTick(() =>
        accessibilityHelper.setFocusKeyboardTrap(this.$el, FOCUS_STACK_LIGHTBOX_CONTEXT, true),
      );
    },
    unbindKeyboardKeys(): void {
      document.removeEventListener('keydown', this.handleKeyboardKeys);

      accessibilityHelper.removeKeyboardTrap(FOCUS_STACK_LIGHTBOX_CONTEXT);
    },
    handleKeyboardKeys(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        this.closeLightbox();
      }
    },
    handleOpen(): void {
      this.lockWindow();
      this.bindKeyboardKeys();
    },
    handleClose(): void {
      this.unlockWindow();
      this.unbindKeyboardKeys();
    },
  },
  watch: {
    isOpen(newValue) {
      if (newValue) {
        this.handleOpen();
      } else {
        this.handleClose();
      }
    },
  },
});
</script>
<style lang="scss">
html.locked,
body.locked {
  margin: 0;

  overflow: hidden;
}
</style>
<style lang="scss">
@import '@owc-styles/utils/z-index-list';

.owc-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  z-index: $z-index-overlay;

  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  overflow: auto;

  background: rgba($wb-black, 0.8);

  &--closed {
    display: none;
  }
  &__close-button {
    position: absolute;
    top: 0;
    right: 0;

    margin: var(--wb-spacing-xs);

    cursor: pointer;
  }
}
</style>
